import { useAuth } from '@/contexts/AuthContext';
import { VerifyAuthResponse } from '@common/api/authService';
import { useUserStorage } from '@common/hooks/useUserStorage';
import React, {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useState
} from 'react';
import { useNavigate } from 'react-router-dom';

interface OnboardingData {
  workspaceId?: string;
  currentStep?: number;
  industry: string;
  otherIndustry: string;
  businessName: string;
  logo: File | string | null;
  logoUrl?: string | null;
  connectedIntegrations: string[];
  plan: string;
  billingCycle: string;
  isTrialStarted: boolean;
}

interface OnboardingContextType {
  startOnboarding: () => void;
  stopOnboarding: () => void;
  nextStep: () => void;
  prevStep: () => void;
  onboardingData: OnboardingData;
  updateOnboardingData: (data: Partial<OnboardingData>) => void;
  // 新增：自动跳转逻辑的控制
  checkOnboardingStatus: (profile: VerifyAuthResponse | null, isLoadingProfile: boolean) => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const useOnboarding = () => {
    const context = useContext(OnboardingContext);
    if (!context) {
        throw new Error('useOnboarding must be used within an OnboardingProvider');
    }
    return context;
};

const ONBOARDING_STORAGE_KEY = 'onboarding_state';

export const OnboardingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const {
        get, set, remove
    } = useUserStorage();
    const navigate = useNavigate();
    const { profile, isLoadingProfile } = useAuth();

    const [onboardingData, setOnboardingData] = useState<OnboardingData>(() => {
        const savedState = get<OnboardingData>(ONBOARDING_STORAGE_KEY);
        return savedState ?? {
            currentStep: 0,
            industry: '',
            otherIndustry: '',
            businessName: '',
            logo: null,
            logoUrl: null,
            connectedIntegrations: [],
            plan: 'Basic',
            billingCycle: 'monthly',
            isTrialStarted: false
        };
    });

    const updateOnboardingData = useCallback((data: Partial<OnboardingData>) => {
        setOnboardingData(prev => {
            const newData = { ...prev, ...data };
            set(ONBOARDING_STORAGE_KEY, newData);
            return newData;
        });
    }, [set]);

    const startOnboarding = () => {
        const initialData = {
            currentStep: 0,
            industry: '',
            otherIndustry: '',
            businessName: '',
            logo: null,
            logoUrl: null,
            connectedIntegrations: [],
            plan: 'Basic',
            billingCycle: 'monthly',
            isTrialStarted: false
        };

        updateOnboardingData(initialData);

        // 跳转到 onboarding 页面
        if (window.location.pathname !== '/onboarding') {
            navigate('/onboarding');
        }
    };

    const stopOnboarding = () => {
        remove(ONBOARDING_STORAGE_KEY);
        updateOnboardingData({ currentStep: 0 });
    };

    const nextStep = useCallback(() => {
        setOnboardingData(prev => {
            const newStep = (prev.currentStep ?? 0) + 1;
            const newData = { ...prev, currentStep: newStep };
            set(ONBOARDING_STORAGE_KEY, newData);
            return newData;
        });
    }, [set]);

    const prevStep = useCallback(() => {
        setOnboardingData(prev => {
            const newStep = Math.max(0, (prev.currentStep ?? 0) - 1);
            const newData = { ...prev, currentStep: newStep };
            set(ONBOARDING_STORAGE_KEY, newData);
            return newData;
        });
    }, [set]);

    // 新增：检查用户是否需要 onboarding
    const checkOnboardingStatus = useCallback((profile: VerifyAuthResponse | null, isLoadingProfile: boolean) => {
        // 未登录，不处理跳转，由 AuthContext 或路由守卫处理
        if(!isLoadingProfile && !profile){
            return;
        }

        if(!isLoadingProfile && profile) {
            if (profile.workspace) {
                // 已有 workspace，说明已完成 onboarding
                // 只有当前在 onboarding 页面时才跳转离开，避免干扰正常访问
                if (window.location.pathname === '/onboarding') {
                    navigate('/');
                }
            } else {
                // 无 workspace，需要 onboarding
                if (window.location.pathname !== '/onboarding') {
                    navigate('/onboarding');
                }
            }
        }
    }, []);
    
    // Check onboarding status when profile changes
    useEffect(() => {
        checkOnboardingStatus(profile, isLoadingProfile);
    }, [profile, isLoadingProfile, checkOnboardingStatus]);

    const value = {
        startOnboarding,
        stopOnboarding,
        nextStep,
        prevStep,
        onboardingData,
        updateOnboardingData,
        checkOnboardingStatus
    };

    return (
        <OnboardingContext.Provider value={value}>
            {children}
        </OnboardingContext.Provider>
    );
};
